/** @format */
import { Heading } from '@mpb-com/feather/components/atoms/heading';
import { HeadingSize } from '@mpb-com/feather/components/atoms/heading/types';
import {
    PillLabel,
    PillLabelSizeVariant,
    PillLabelVariant,
} from '@mpb-com/feather/components/atoms/pill-label';
import { useTranslation } from 'next-i18next';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import ArrowLeftIcon from '@toucan/client/global/icons/arrow-left.svg';
import ArrowRightIcon from '@toucan/client/global/icons/arrow-right.svg';

import {
    ConstrainedWrapper,
    ConstrainedWrapperType,
} from '@toucan/client/shared/components/constrained-wrapper';
import { trackViaDataLayer } from '@toucan/client/shared/helpers/analytics';

import { scrollGentlyIntoView } from '../../../helpers';
import {
    DEFAULT_TEST_ID,
    JOURNEY_CARD_EVENT,
    TimelineSide,
} from '../../config/constants';
import { ModelJourneyFanProps } from '../../config/types';
import { findFanSelection, getFanModels } from '../../helpers';
import { FanHand } from '../fan-hand';
import { JourneyFeedback } from '../journey-feedback';
import { TimelineDetail } from '../timeline-detail';
import { TimelineMarker } from '../timeline-marker';
import { styles } from './fan-section.styles';

const StyledFanSection = styled.section`
    ${styles}
`;

export const FanSection: FunctionComponent<ModelJourneyFanProps> = ({
    journey,
    modelName,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
    ...otherProps
}) => {
    const { t } = useTranslation();
    const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
    const detailRef = useRef<HTMLDivElement>(null);

    /*
     * Counts presses rather than tracking which card is drawn, so that pressing
     * the card already drawn scrolls to it again — the reader has asked to be
     * shown it either way, and the selected model on its own would not have
     * changed to say so.
     */
    const [pressCount, setPressCount] = useState(0);

    /**
     * The detail card sits below both hands, and on a phone below a whole
     * vertical list of them, so a reader picking a card near the top can easily
     * not see the thing their press just filled in. Bringing it into view is the
     * only way the press has a visible result at that scroll position.
     *
     * The scroll goes the least distance that makes the card visible and takes its
     * time over it — see scrollGentlyIntoView, which owns both of those because the
     * browser's own smooth scrolling decides its speed for itself.
     *
     * It has to wait for the render the press causes. The distance is measured
     * once, from where the detail card is at the time of asking, and until the
     * first press that card is a placeholder of a different height — so measuring
     * in the handler would aim the very first scroll of the section at where the
     * placeholder was rather than at the card that replaced it.
     */
    useEffect(() => {
        if (!pressCount) {
            return;
        }

        scrollGentlyIntoView(detailRef.current);
    }, [pressCount]);

    const handleSelect = (modelId: number, side: TimelineSide) => {
        setSelectedModelId(modelId);
        setPressCount((count) => count + 1);

        /*
         * Which hand the card was drawn from is reported alongside it, because the
         * same model can sit in both: "drew model 123" says much less than "drew
         * model 123 from the models people owned first".
         *
         * It is the card that was pressed that is reported, not what is now selected
         * — pressing the card already drawn reports it again, since that is a reader
         * pressing a card either way.
         */
        trackViaDataLayer({
            event: JOURNEY_CARD_EVENT,
            model_id: journey.modelId,
            card_model_id: modelId,
            card_side: side,
        });
    };

    /*
     * A handler per hand, so each supplies the side its own cards were drawn from.
     * FanHand knows which side it is and reports only the model, which keeps the
     * side out of the props of every card.
     */
    const handleBeforeSelect = (modelId: number) =>
        handleSelect(modelId, TimelineSide.BEFORE);

    const handleAfterSelect = (modelId: number) =>
        handleSelect(modelId, TimelineSide.AFTER);

    const selection = findFanSelection(journey, selectedModelId);
    const before = getFanModels(journey.precededBy);
    const after = getFanModels(journey.followedBy);
    const hasBefore = before.length > 0;
    const hasAfter = after.length > 0;

    return (
        <StyledFanSection data-testid={dataTestId} {...otherProps}>
            <ConstrainedWrapper type={ConstrainedWrapperType.PADDING}>
                <div className="fan-section-header">
                    {/*
                     * The section's own name, beside the beta pill rather than
                     * in place of the heading below it: the heading says what
                     * this particular reading of the data is, and this says
                     * which of the two insights sections it belongs to. Kit
                     * intelligence is introduced the same way, so the pair read
                     * as two parts of one thing.
                     */}
                    <div className="fan-section-eyebrow-row">
                        <span className="fan-section-eyebrow">
                            {t('Kit journey')}
                        </span>
                        <PillLabel
                            variant={PillLabelVariant.SUBTLE}
                            size={PillLabelSizeVariant.SMALL}
                        >
                            {t('Beta')}
                        </PillLabel>
                    </div>
                    <Heading
                        size={HeadingSize.H2}
                        className="fan-section-heading"
                    >
                        {t(
                            'How visual storytellers get here and where they go next.'
                        )}
                    </Heading>
                    <p className="fan-section-intro">
                        {t(
                            'Photographers come to the {{modelName}} from all kinds of gear, then move on as their needs change.',
                            { modelName }
                        )}
                    </p>
                </div>

                <div className="fan-section-track">
                    <span
                        className="fan-section-line fan-section-line--past"
                        aria-hidden="true"
                    />
                    <span
                        className="fan-section-line fan-section-line--future"
                        aria-hidden="true"
                    />

                    <p className="fan-section-label fan-section-label--past">
                        <span className="fan-section-label-text">
                            <ArrowLeftIcon />
                            {t(
                                'The gear photographers owned before choosing this'
                            )}
                        </span>
                    </p>

                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {hasBefore && (
                        <div className="fan-section-side fan-section-side--before">
                            <FanHand
                                className="fan-section-hand"
                                models={before}
                                side={TimelineSide.BEFORE}
                                selectedModelId={selectedModelId}
                                onSelect={handleBeforeSelect}
                                data-testid={`${dataTestId}__before-hand`}
                            />
                            <span
                                className="fan-section-dot"
                                aria-hidden="true"
                            />
                        </div>
                    )}
                    <TimelineMarker
                        className="fan-section-marker"
                        modelName={modelName}
                        data-testid={`${dataTestId}__marker`}
                    />
                    {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                    {hasAfter && (
                        <div className="fan-section-side fan-section-side--after">
                            <FanHand
                                className="fan-section-hand"
                                models={after}
                                side={TimelineSide.AFTER}
                                selectedModelId={selectedModelId}
                                onSelect={handleAfterSelect}
                                data-testid={`${dataTestId}__after-hand`}
                            />
                            <span
                                className="fan-section-dot"
                                aria-hidden="true"
                            />
                        </div>
                    )}

                    <p className="fan-section-label fan-section-label--future">
                        <span className="fan-section-label-text">
                            {t('What they buy next')}
                            <ArrowRightIcon />
                        </span>
                    </p>
                </div>

                {/* Wrapper exists to be the scroll target — see handleSelect. */}
                <div className="fan-section-detail-anchor" ref={detailRef}>
                    <TimelineDetail
                        className="fan-section-detail"
                        selection={selection}
                        data-testid={`${dataTestId}__detail`}
                    />
                </div>

                <JourneyFeedback
                    modelId={journey.modelId}
                    data-testid={`${dataTestId}__feedback`}
                />
            </ConstrainedWrapper>
        </StyledFanSection>
    );
};
